/**
 * MinhaForjaCard - "Seu Escritório" section card
 * Highlights Minha Forja as the main workspace with activity info
 * Shows locked state for visitors with CTA to become a member
 */

import React, { useState } from 'react';
import { formatPrice as formatCurrency } from '@/lib/formatCurrency';
import { Link } from 'react-router-dom';
import { Hammer, FileText, CheckSquare, ArrowRight, Clock, Sparkles, TrendingUp, Eye, EyeOff, Lock, ExternalLink, Plus } from 'lucide-react';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRole } from '@/contexts/RoleContext';

interface LastActivity {
  type: 'note' | 'task';
  title: string;
  timeAgo: string;
}

interface MinhaForjaCardProps {
  notesCount: number;
  tasksCount: number;
  lastActivity?: LastActivity | null;
  monthlyEarnings?: number;
  onAddEarning?: () => void;
}


function VisitorCTA() {
  const { redirectToPV } = usePVRedirect();
  return (
    <Button size="sm" className="gap-2" onClick={() => redirectToPV('dashboard-forja')}>
      Quero ser Membro
      <ExternalLink className="w-3.5 h-3.5" />
    </Button>
  );
}

function MinhaForjaCardInner({ notesCount, tasksCount, lastActivity, monthlyEarnings, onAddEarning }: MinhaForjaCardProps) {
  const { effectiveRole } = useRole();
  const isVisitor = effectiveRole === 'visitor';
  const isEmpty = notesCount === 0 && tasksCount === 0;
  
  const [isEarningsHidden, setIsEarningsHidden] = useState(() => {
    return localStorage.getItem('forja:earnings-hidden') === 'true';
  });

  const toggleEarningsVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newValue = !isEarningsHidden;
    setIsEarningsHidden(newValue);
    localStorage.setItem('forja:earnings-hidden', String(newValue));
  };

  // Locked state for visitors
  if (isVisitor) {
    return (
      <div className={cn(
        "h-full relative overflow-hidden rounded-xl md:rounded-2xl p-4 md:p-6",
        "forja-card"
      )}>
          <div className="relative z-10 text-center space-y-4">
            {/* Lock Icon */}
            <div className="w-12 h-12 mx-auto rounded-xl bg-muted/50 flex items-center justify-center">
              <Lock className="w-6 h-6 text-muted-foreground/60" />
            </div>
            
            {/* Message */}
            <div>
              <h3 className="font-display font-semibold text-sm md:text-base text-foreground mb-1">
                Área Exclusiva
              </h3>
               <p className="text-xs text-muted-foreground">
                Disponível para membros da comunidade Forja.
               </p>
            </div>
            
            <VisitorCTA />
          </div>
        </div>
    );
  }

  return (
      <Link to="/minha-forja" className="block h-full">
        <div className={cn(
          "h-full relative overflow-hidden rounded-xl md:rounded-2xl p-3 md:p-4 flex flex-col",
          "forja-card",
          "transition-all duration-300 group"
        )}>

          <div className="relative z-10 flex flex-col flex-1">
            {/* Icon + Title */}
            <div className="flex items-start gap-2.5 mb-3">
              <div className="icon-wrapper w-8 h-8">
                <Hammer className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-bold text-sm md:text-base text-foreground leading-tight">
                  Minha Forja
                </h3>
                <p className="text-[9px] md:text-[10px] text-muted-foreground">
                  Gerencie ganhos, tarefas e estratégias do seu negócio.
                </p>
              </div>
            </div>

            {/* Quick Stats - Clickable shortcuts */}
            <div className="flex items-center gap-1.5 md:gap-2 mb-2.5 flex-wrap">
              <Link 
                to="/minha-forja?tab=forja-notes"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-secondary/80 border border-border/50 hover:border-primary/40 hover:bg-secondary transition-all group/stat"
                onClick={(e) => e.stopPropagation()}
              >
                <FileText className="w-3 h-3 text-muted-foreground group-hover/stat:text-primary transition-colors" />
                <span className="text-[11px] font-medium text-foreground">{notesCount}</span>
                <span className="text-[9px] text-muted-foreground hidden sm:inline">notas</span>
              </Link>
              <Link 
                to="/minha-forja?tab=forja-tasks"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-secondary/80 border border-border/50 hover:border-primary/40 hover:bg-secondary transition-all group/stat"
                onClick={(e) => e.stopPropagation()}
              >
                <CheckSquare className="w-3 h-3 text-muted-foreground group-hover/stat:text-primary transition-colors" />
                <span className="text-[11px] font-medium text-foreground">{tasksCount}</span>
                <span className="text-[9px] text-muted-foreground hidden sm:inline">tarefas</span>
              </Link>
              
              {/* Monthly Earnings Badge */}
              {monthlyEarnings !== undefined && monthlyEarnings > 0 && (
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-success/10 border border-success/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TrendingUp className="w-3 h-3 text-success" />
                  <span className="text-[11px] font-medium text-success">
                    {isEarningsHidden ? '•••••••' : formatCurrency(monthlyEarnings)}
                  </span>
                  <button
                    onClick={toggleEarningsVisibility}
                    className="ml-0.5 p-0.5 rounded hover:bg-success/20 transition-colors"
                    title={isEarningsHidden ? 'Mostrar faturamento' : 'Ocultar faturamento'}
                  >
                    {isEarningsHidden ? (
                      <EyeOff className="w-3 h-3 md:w-3.5 md:h-3.5 text-success/70" />
                    ) : (
                      <Eye className="w-3 h-3 md:w-3.5 md:h-3.5 text-success/70" />
                    )}
                  </button>
                  <span className="text-[8px] text-success/60 hidden sm:inline">mês</span>
                </div>
              )}
              
              {/* Add Earning Shortcut Button */}
              {onAddEarning && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddEarning();
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 border border-dashed border-primary/30 hover:border-primary/50 hover:bg-primary/15 transition-all group/add"
                >
                  <Plus className="w-3 h-3 text-primary group-hover/add:scale-110 transition-transform" />
                  <span className="text-[11px] font-medium text-primary">Ganho</span>
                </button>
              )}
            </div>

            {/* Last Activity OR Empty State */}
            {lastActivity ? (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-secondary/50 border border-border/30">
                <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-[11px] text-muted-foreground truncate">
                  Última {lastActivity.type === 'note' ? 'nota' : 'tarefa'}:{' '}
                  <span className="text-foreground/80">{lastActivity.title}</span>
                </span>
                <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">
                  {lastActivity.timeAgo}
                </span>
              </div>
            ) : isEmpty ? (
              <div className="flex items-center gap-2 mb-2 px-3 py-3 rounded-lg bg-secondary/30 border border-dashed border-border/40">
                <Sparkles className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                <span className="text-xs text-muted-foreground">
                  Crie sua primeira nota ou tarefa para começar.
                </span>
              </div>
            ) : null}

            {/* CTA */}
            <div className="mt-auto pt-2">
            <Button size="sm" className="w-full gap-1.5 text-xs group/btn">
              Abrir centro de operação
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5" />
            </Button>
            </div>
          </div>
        </div>
      </Link>
  );
}

const MinhaForjaCard = React.memo(MinhaForjaCardInner);
export default MinhaForjaCard;
